import { Link } from 'react-router-dom';
import { servicesData } from '../../../mocks/servicesData';

interface ServiceDetailProps {
  service: {
    id: string;
    icon: string;
    title: string;
    tagline: string;
    description: string;
    features: string[];
    image: string;
    gallery: string[];
  };
  index: number;
}

export default function ServiceDetail({ service, index }: ServiceDetailProps) {
  const isReversed = index % 2 !== 0;
  const relatedServices = servicesData.filter((s) => s.id !== service.id).slice(0, 3);

  return (
    <section
      id={service.id}
      className={`py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50'}`}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
          <Link to="/" className="hover:text-amber-600 transition-colors whitespace-nowrap">Home</Link>
          <i className="ri-arrow-right-s-line text-gray-400"></i>
          <Link to="/services" className="hover:text-amber-600 transition-colors whitespace-nowrap">Services</Link>
          <i className="ri-arrow-right-s-line text-gray-400"></i>
          <span className="text-gray-900 font-medium whitespace-nowrap">{service.title}</span>
        </nav>

        <div className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-12 items-center mb-12`}>
          {/* Image Side */}
          <div className="w-full lg:w-1/2">
            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <div className="h-[400px] w-full overflow-hidden">
                <img
                  src={service.image}
                  alt={`${service.title} - Sierra Kustom Inc. Edmonton Alberta`}
                  title={`${service.title} professional service by Sierra Kustom Inc.`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute top-5 left-5">
                <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className={`${service.icon} text-amber-600 text-lg`}></i>
                  </div>
                  <span className="text-sm font-bold text-gray-900 whitespace-nowrap">{service.title}</span>
                </div>
              </div>
            </div>

            {/* Mini Gallery */}
            <div className="grid grid-cols-2 gap-4 mt-4">
              {service.gallery.map((img, i) => (
                <div key={i} className="rounded-xl overflow-hidden shadow-md group/gal">
                  <div className="h-40 w-full overflow-hidden">
                    <img
                      src={img}
                      alt={`${service.title} project example ${i + 1}`}
                      title={`${service.title} craftsmanship detail`}
                      className="w-full h-full object-cover object-top group-hover/gal:scale-110 transition-transform duration-500"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Content Side */}
          <div className="w-full lg:w-1/2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded-2xl flex items-center justify-center">
                <i className={`${service.icon} text-amber-600 text-2xl`}></i>
              </div>
              <span className="text-amber-600 font-semibold text-sm tracking-wide uppercase">{service.tagline}</span>
            </div>

            <h2 className="text-4xl font-bold text-gray-900 mb-5 tracking-tight leading-tight">
              {service.title}
            </h2>

            <p className="text-gray-600 leading-relaxed mb-8 text-base">
              {service.description}
            </p>

            <div className="mb-8">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">What We Offer</h3>
              <div className="grid grid-cols-1 gap-3">
                {service.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-500/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-amber-600 text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mb-6">
              <a
                href="tel:7805178242"
                className="bg-gradient-to-r from-amber-600 to-amber-500 text-white px-7 py-3 rounded-full hover:from-amber-700 hover:to-amber-600 transition-all cursor-pointer font-semibold text-sm whitespace-nowrap shadow-lg shadow-amber-600/30 flex items-center gap-2"
              >
                <i className="ri-phone-fill"></i>
                Get a Quote
              </a>
              <a
                href="mailto:Info@sierrakustoms.ca"
                className="border-2 border-gray-200 text-gray-700 px-7 py-3 rounded-full hover:border-amber-500 hover:text-amber-600 transition-all cursor-pointer font-semibold text-sm whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-mail-line"></i>
                Email Us
              </a>
            </div>

            {/* Quick links */}
            <div className="flex flex-wrap gap-2">
              <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-amber-600 transition-colors border border-gray-200 px-3 py-1.5 rounded-full hover:border-amber-300 whitespace-nowrap">
                <i className="ri-home-line text-xs"></i> Home
              </Link>
              <Link to="/services" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-amber-600 transition-colors border border-gray-200 px-3 py-1.5 rounded-full hover:border-amber-300 whitespace-nowrap">
                <i className="ri-list-check text-xs"></i> All Services
              </Link>
              <a href="tel:7805178242" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-amber-600 transition-colors border border-gray-200 px-3 py-1.5 rounded-full hover:border-amber-300 whitespace-nowrap">
                <i className="ri-phone-line text-xs"></i> 780-517-8242
              </a>
            </div>
          </div>
        </div>

        {/* Related Services */}
        <div className="border-t border-gray-100 pt-10">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-5">Also Explore</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {relatedServices.map((related) => (
              <Link
                key={related.id}
                to={`/services#${related.id}`}
                className="flex items-center gap-4 p-4 rounded-2xl border border-gray-100 hover:border-amber-200 hover:bg-amber-50/50 transition-all group/related cursor-pointer"
              >
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0 group-hover/related:bg-amber-200 transition-colors">
                  <i className={`${related.icon} text-amber-600 text-lg`}></i>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-gray-900 leading-tight whitespace-nowrap">{related.title}</p>
                  <p className="text-xs text-gray-500 whitespace-nowrap">{related.tagline}</p>
                </div>
                <div className="w-5 h-5 flex items-center justify-center ml-auto flex-shrink-0">
                  <i className="ri-arrow-right-line text-amber-500 text-sm group-hover/related:translate-x-1 transition-transform"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
